<script setup>
import { computed, nextTick, reactive, ref, watch } from "vue";
import { Close } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { storeToRefs } from "pinia";
import { useLayoutStore } from "@/stores/layout";
import {
  addFeedback,
  editFeedback,
  getFeedbackById,
  feedBackRightRemaining,
  GetFeeBackType,
  normalizeFeeBackTypes,
} from "@/api/feedBack";
import { uploader } from "@/utils/patchUpload.js";

const layoutStore = useLayoutStore();
const { feedbackDialog } = storeToRefs(layoutStore);

const visible = computed({
  get: () => !!feedbackDialog.value.dialog,
  set: (v) => {
    if (!v) layoutStore.closeFeedbackDialog();
    else layoutStore.openFeedbackDialog(feedbackDialog.value.id);
  },
});

const formRef = ref(null);
const fileInputRef = ref(null);
const submitting = ref(false);
const remainingNum = ref(0);
const appealTypes = ref([]);
const editMode = ref(false);
const editFeedbackId = ref("");

const form = reactive({
  AppealType: "",
  ClassID: "",
  ComplaintName: "",
  AppealContent: "",
  AppealFileName: "",
});
const appealFiles = ref([]);

const needClassId = computed(() => [1, 2, 4, 9, 10, 12, 13, 36].includes(Number(form.AppealType)));
const needComplaintName = computed(() => Number(form.AppealType) === 11);

const rules = {
  AppealType: [{ required: true, message: "Required.", trigger: "change" }],
  AppealContent: [
    { required: true, message: "Required.", trigger: "blur" },
    {
      validator: (_r, v, cb) => {
        if (String(v || "").length > 500) cb(new Error("Max 500 characters"));
        else cb();
      },
      trigger: "blur",
    },
  ],
  AppealFileName: [{ required: true, message: "Required.", trigger: "change" }],
  ClassID: [
    {
      validator: (_r, v, cb) => {
        if (Number(form.AppealType) === 1) return cb();
        if (needClassId.value && !v) return cb(new Error("Required."));
        cb();
      },
      trigger: "blur",
    },
  ],
  ComplaintName: [
    {
      validator: (_r, v, cb) => {
        if (needComplaintName.value && !String(v || "").trim()) {
          return cb(new Error("Required."));
        }
        cb();
      },
      trigger: "blur",
    },
  ],
};

function resetForm() {
  form.AppealType = "";
  form.ClassID = "";
  form.ComplaintName = "";
  form.AppealContent = "";
  form.AppealFileName = "";
  appealFiles.value = [];
  editMode.value = false;
  editFeedbackId.value = "";
  nextTick(() => formRef.value?.clearValidate());
}

function loadTypes() {
  GetFeeBackType()
    .then((res) => {
      appealTypes.value = normalizeFeeBackTypes(res?.data?.Body);
    })
    .catch(() => {
      appealTypes.value = [];
    });
}

function loadRemaining() {
  feedBackRightRemaining()
    .then((res) => {
      if (res?.data?.ErrorCode === 0) remainingNum.value = res.data.Body || 0;
    })
    .catch(() => {
      remainingNum.value = 0;
    });
}

function loadById(id) {
  getFeedbackById(id)
    .then((res) => {
      const r = res?.data?.Body;
      if (!r) return;
      form.AppealType = r.Type;
      form.AppealContent = r.Remark;
      form.AppealFileName = r.FileUrl;
      editMode.value = true;
      editFeedbackId.value = id;
    })
    .catch(() => {});
}

function pickFile() {
  if (fileInputRef.value) fileInputRef.value.value = "";
  fileInputRef.value?.click();
}

function onFileChange(e) {
  const files = e.target.files;
  if (!files?.length) return;
  appealFiles.value = files;
  form.AppealFileName = files[0].name;
}

async function submit() {
  try {
    await formRef.value?.validate();
  } catch (_) {
    return;
  }
  if (submitting.value) return;

  const param = {
    Type: form.AppealType,
    Clid: form.ClassID,
    Respondents: form.ComplaintName,
    Remark: form.AppealContent,
  };

  try {
    submitting.value = true;
    if (appealFiles.value?.length) {
      const _uploader = uploader({ module: "AppealFile" });
      const uploadResult = await _uploader.uploadBatchWithKey([
        { key: "appealFileFlag", file: appealFiles.value },
      ]);
      if (uploadResult.ErrorCode != 0) {
        ElMessage.error(uploadResult.Msg || "Upload fail.");
        return;
      }
      const screenshotData = uploadResult.Body;
      if (screenshotData?.appealFileFlag?.[0]) {
        param.File = screenshotData.appealFileFlag[0];
      }
    }

    const api = editMode.value ? editFeedback : addFeedback;
    if (editMode.value) param.ID = editFeedbackId.value;
    const res = await api(param);
    const ok = res?.data?.Body?.Item1;
    const msg = res?.data?.Body?.Item2 || "";
    if (ok) {
      ElMessage.success(msg || "Success");
      layoutStore.closeFeedbackDialog();
    } else {
      ElMessage.warning(msg || "Failed");
    }
  } catch (e) {
    ElMessage.error(e?.message || "Submit failed");
  } finally {
    submitting.value = false;
  }
}

watch(
  () => feedbackDialog.value.dialog,
  (open) => {
    if (open) {
      resetForm();
      loadTypes();
      loadRemaining();
      if (feedbackDialog.value.id) loadById(feedbackDialog.value.id);
    }
  }
);
</script>

<template>
  <el-dialog
    v-model="visible"
    width="700px"
    align-center
    append-to-body
    :close-on-click-modal="false"
    :show-close="false"
    class="feedback-create-dialog pb-5"
  >
    <template #header>
      <div class="dialog-header">
        <span>Feedback</span>
        <button type="button" class="close-btn" @click="layoutStore.closeFeedbackDialog()">
          <el-icon :size="18"><Close /></el-icon>
        </button>
      </div>
    </template>

    <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
      <el-form-item label="Type" prop="AppealType">
        <el-select
          v-model="form.AppealType"
          filterable
          placeholder="Type"
          class="full type-select"
          popper-class="feedback-type-popper"
        >
          <el-option
            v-for="opt in appealTypes"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
            :disabled="opt.disabled"
          />
        </el-select>
      </el-form-item>

      <template v-if="needClassId">
        <el-form-item label="Class ID" prop="ClassID">
          <el-input
            v-model="form.ClassID"
            :placeholder="
              Number(form.AppealType) === 1
                ? 'If you have concerns about the salary of a certain class, please fill the Class ID.'
                : ''
            "
          />
        </el-form-item>
      </template>

      <el-form-item v-if="needComplaintName" label="Complaint Name" prop="ComplaintName">
        <el-input v-model="form.ComplaintName" />
      </el-form-item>

      <el-form-item label="Content" prop="AppealContent">
        <el-input
          v-model="form.AppealContent"
          type="textarea"
          :rows="5"
          maxlength="500"
          show-word-limit
          resize="none"
        />
      </el-form-item>

      <div class="upload-row">
        <el-form-item label="File Upload" prop="AppealFileName" class="upload-name">
          <el-input v-model="form.AppealFileName" readonly />
        </el-form-item>
        <button type="button" class="tutor-blue-btn upload-btn mb-0" @click="pickFile">
          Click to Upload
        </button>
        <input
          ref="fileInputRef"
          type="file"
          accept=".jpg,.png,.jpeg,.gif"
          class="hidden-file"
          @change="onFileChange"
        />
      </div>

      <p v-if="remainingNum" class="remaining">
        Number of remaining fast feedback channels:{{ remainingNum }}
      </p>
    </el-form>

    <template #footer>
      <div class="footer-actions mb-5 mt-5 pt-5">
        <button type="button" class="tutor-red-btn" :disabled="submitting" @click="submit">
          Submit
        </button>
        <button type="button" class="cancel-btn" @click="layoutStore.closeFeedbackDialog()">
          Cancel
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  color: #32397a;
  font-size: 18px;
  font-weight: 700;
}

.close-btn {
  border: 0;
  background: transparent;
  cursor: pointer;
  color: #999;
  padding: 0;
}

.full {
  width: 100%;
}

/* Type select — same 40px height as File Upload input */
.type-select {
  :deep(.el-select__wrapper) {
    min-height: 40px;
    height: 40px;
    padding-top: 0;
    padding-bottom: 0;
  }
}

.upload-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.upload-name {
  flex: 1;
  margin-bottom: 0;

  /* taller file-name input (align with Vue2 outline field ~38–40px) */
  :deep(.el-input__wrapper) {
    min-height: 40px;
    height: 40px;
    padding-top: 0;
    padding-bottom: 0;
  }

  :deep(.el-input__inner) {
    height: 40px;
    line-height: 40px;
  }
}

/* Vue2 dialog .upload-btn: height 38px, wider click target; match input height */
.upload-btn.tutor-blue-btn {
  flex-shrink: 0;
  height: 40px !important;
  min-width: 200px !important;
  padding: 0 20px !important;
  line-height: 40px !important;
  margin-bottom: 0;
  box-sizing: border-box;
  font-size: 16px;
}

.hidden-file {
  display: none;
}

.remaining {
  color: #f69898;
  margin: 0 0 8px;
}

.footer-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.cancel-btn {
  height: 28px;
  min-width: 80px;
  border: 0;
  border-radius: 4px;
  background: #e0e0e0;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}
</style>

<style lang="scss">
/* Soft Mode — Feedback 弹层为暗色弹层样式基准 */
[data-theme="theme1"] .feedback-create-dialog.el-dialog {
  background: #1a1f32 !important;
  border: 1px solid #383a77;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.2) !important;
}

[data-theme="theme1"] .feedback-create-dialog {
  .el-dialog__header,
  .el-dialog__body,
  .el-dialog__footer {
    background: #1a1f32 !important;
  }

  .dialog-header {
    color: #ececec !important;
  }

  .close-btn {
    color: rgba(255, 255, 255, 0.55) !important;
  }

  .el-form-item__label {
    color: #ececec !important;
  }

  .el-input__wrapper,
  .el-textarea__inner,
  .el-select__wrapper {
    background-color: #1a1f32 !important;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.2) inset !important;
  }

  .el-input__inner,
  .el-textarea__inner,
  .el-select__selected-item {
    color: #ececec !important;
  }

  .cancel-btn {
    background: #e0e0e0 !important;
    color: #fff !important;
  }
}
</style>
